import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useGalerie, useTrip } from '../../hooks/queries'
import type { Galeriebild } from '../../api/types'
import { fehlertext } from '../../api/client'
import { buchTag, heuteSchluessel, type Uhr } from '../../lib/ortszeit'
import { dauerText, standbildAdresse } from '../../lib/video'
import { BackIcon } from '../../components/Icons'
import { Netzstreifen } from '../../components/Netzstreifen'
import { Standbild } from '../../components/Standbild'
import { Lightbox } from '../thread/Gallery'
import { useTexte } from '../../texte'

/**
 * All pictures and videos of a trip (1.20.0) -- the category "Fotos".
 *
 * A grid by day, newest day first; three tiles a row on a phone, six on a
 * tablet.
 *
 * **The day is the day it was taken, in the clock of that place** (1.91.0).
 * From use, with a screenshot: Tuesday, Wednesday, Tuesday, Wednesday. The
 * server ordered by upload while the day came from the post or the point,
 * and the day was read in the device's clock -- on an iPad in Germany, an
 * evening in New England is already the next day. Now the server orders by
 * one moment and gives its clock along; a row without one reads the trip's
 * zone course, like everywhere (`docs/zeit.md`). The heading is the full day
 * ("Dienstag, 29. September 2026"), because "Dienstag" alone says nothing a
 * week later. And days are collected, not cut at each change: where a zone
 * change puts a picture out of step, it still lands under its day. A tap on a
 * picture opens the same large view as in the chat (`Lightbox`), with all
 * loaded pictures to leaf through; a tap on a video plays it in place
 * (1.20.1 -- from use: "für Videos: springt in den Chat"), with a way to
 * the post or the point below it. Until then a tap on a video went where the video
 * plays -- the chat at its message, or the map at its point -- because the
 * large view shows pictures only. Loaded in pages of a hundred and twenty,
 * one more per tap on "Mehr laden".
 *
 * **The server decides what is here.** Every tile went through
 * `Visibility` twice over -- by its message or by its point; this page
 * shows what it gets.
 */
export function FotosPage() {
  const texte = useTexte()
  const t = texte.fotos
  const { tripId: roh } = useParams()
  const tripId = Number(roh)
  const galerie = useGalerie(tripId)
  const verlauf = useTrip(tripId).data?.zones ?? null
  const [offen, setOffen] = useState<number | null>(null)

  const alle: Galeriebild[] = galerie.data?.pages.flatMap((p) => p.media) ?? []

  // Grouped by day, in the order the server gave (newest first).
  const tage: Array<{ key: string; titel: string; eintraege: Galeriebild[] }> = []
  const nachTag = new Map<string, Galeriebild[]>()
  for (const m of alle) {
    const uhr: Uhr = m.tz_offset_min ?? verlauf
    const key = m.at === null ? '' : heuteSchluessel(uhr, m.at)
    const vorhanden = nachTag.get(key)
    if (vorhanden !== undefined) {
      vorhanden.push(m)
      continue
    }
    const eintraege = [m]
    nachTag.set(key, eintraege)
    tage.push({ key, titel: m.at === null ? t.ohneTag : buchTag(m.at, uhr), eintraege })
  }

  /**
   * Antippen öffnet die Großansicht -- **für Fotos wie für Videos** (#86,
   * 1.56.0).
   *
   * Bis dahin spielte ein Video hier in einem eigenen Blatt, und die
   * Großansicht kannte es nicht: von einem Foto zum Video zu blättern ging
   * nicht, obwohl beide in derselben Reihe standen. Jetzt ist es eine Reihe
   * und ein Blatt; der Weg zum Beitrag oder zum POI steht in seiner Leiste.
   */
  function oeffnen(m: Galeriebild) {
    setOffen(alle.findIndex((x) => x.id === m.id))
  }

  /** Wo ein Medium hängt: sein Beitrag im Chat oder sein POI auf der Karte. */
  function heimat(mediaId: number): { ziel: string; wort: string } | null {
    const m = alle.find((x) => x.id === mediaId)
    if (m === undefined) return null
    if (m.message_id !== null) return { ziel: `/t/${tripId}?m=${m.message_id}`, wort: t.zumBeitrag }
    if (m.highlight_id !== null) return { ziel: `/t/${tripId}/karte?punkt=${m.highlight_id}`, wort: t.zumPunkt }
    return null
  }

  return (
    <div className="fotos" data-fotos="">
      <Netzstreifen />
      <header className="bar">
        <Link className="icon-button" to={`/t/${tripId}/info`} aria-label={t.zurueck}>
          <BackIcon />
        </Link>
        <h2 className="bar__title bar__grow">{t.titel}</h2>
      </header>
      <div className="fotos__inhalt">
        {galerie.isError && (
          <p className="notice" role="alert">{fehlertext(galerie.error)}</p>
        )}
        {galerie.data !== undefined && alle.length === 0 && <p className="leise">{t.keine}</p>}
        {tage.map((tag) => (
          <section key={tag.key} data-fotos-tag={tag.key}>
            <h3 className="fotos__ueberschrift">{tag.titel}</h3>
            <div className="fotos__raster">
              {tag.eintraege.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="fotos__kachel"
                  onClick={() => oeffnen(m)}
                  aria-label={m.kind === 'video' ? t.videoOeffnen : t.bildOeffnen}
                  data-fotos-kind={m.kind}
                  data-fotos-id={m.id}
                >
                  <Standbild src={standbildAdresse(m) ?? m.url} loading="lazy" />
                  {m.kind === 'video' && (
                    <span className="fotos__abspielen" aria-hidden="true">
                      ▶ {m.duration_ms > 0 ? dauerText(m.duration_ms) : ''}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </section>
        ))}
        {galerie.hasNextPage && (
          <button
            type="button"
            className="button button--outline"
            disabled={galerie.isFetchingNextPage}
            onClick={() => void galerie.fetchNextPage()}
            data-fotos-mehr=""
          >
            {galerie.isFetchingNextPage ? texte.allgemein.laeuft : t.mehr}
          </button>
        )}
      </div>

      {offen !== null && alle[offen] !== undefined && (
        <Lightbox
          media={alle}
          start={offen}
          reactionsFor={() => []}
          canReact={false}
          onReact={() => undefined}
          wegZu={heimat}
          onClose={() => setOffen(null)}
        />
      )}
    </div>
  )
}
